// Phase 24d · User's tickets — Upcoming / Past / Canceled
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Ticket, QrCode, XCircle, Clock, Calendar, ArrowLeft } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function eur(c) { return `$${(Number(c || 0) / 100).toFixed(2)}`; }
function fmt(iso) { try { return new Date(iso).toLocaleString([], { dateStyle: "short", timeStyle: "short" }); } catch { return iso; } }

function refundCountdown(deadlineIso) {
    if (!deadlineIso) return null;
    try {
        const d = new Date(deadlineIso).getTime();
        const now = Date.now();
        if (d <= now) return null;
        const mins = Math.floor((d - now) / 60000);
        if (mins < 60) return `${mins}m`;
        return `${Math.floor(mins / 60)}h ${mins % 60}m`;
    } catch { return null; }
}

function TicketRow({ t, onAction }) {
    const [qrOpen, setQrOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const refundStr = refundCountdown(t.refund_deadline_at);
    const cancelable = ["confirmed"].includes(t.status) && refundStr;
    const cancel = async () => {
        if (busy) return;
        setBusy(true);
        try {
            const r = await fetch(`${API}/api/tickets/me/${t.ticket_id}/cancel`, {
                method: "POST", credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ reason: "user_cancel" }),
            });
            const d = await r.json().catch(() => ({}));
            if (r.ok) { toast.success("Refund requested"); onAction?.(); }
            else toast.error(d.detail?.error || "Cancel failed");
        } catch { toast.error("Network error"); }
        setBusy(false);
    };
    return (
        <div className="bg-[#111111] border border-[#1A1A1A] rounded-xl p-4 space-y-2" data-testid={`ticket-row-${t.ticket_id}`}>
            <div className="flex items-center justify-between">
                <div>
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-bold ${
                        t.status === "confirmed" ? "bg-[#CCCCCC]/10 text-[#CCCCCC]" :
                        t.status === "refunded" ? "bg-neutral-700/20 text-neutral-400" :
                        t.status === "canceled" ? "bg-neutral-700/20 text-neutral-400" :
                        "bg-[#FAFAFA]/10 text-[#FAFAFA]"
                    }`}>
                        {t.status}
                    </span>
                </div>
                <span className="text-sm font-bold text-neutral-200">{eur(t.price_paid_cents)}</span>
            </div>
            <p className="text-xs text-neutral-400 flex items-center gap-2 flex-wrap">
                <Calendar size={12} /> Purchased {fmt(t.purchased_at)}
                {t.holder_email && <span className="text-neutral-300">· Gifted to {t.holder_email}</span>}
                {t.checked_in_at && <span className="text-[#CCCCCC]">· Checked in {fmt(t.checked_in_at)}</span>}
            </p>
            {refundStr && t.status === "confirmed" && (
                <p className="text-[11px] text-[#CCCCCC] flex items-center gap-1" data-testid={`ticket-refund-countdown-${t.ticket_id}`}>
                    <Clock size={11} /> Refund available for {refundStr}
                </p>
            )}
            <div className="flex items-center gap-2 pt-2 border-t border-[#1A1A1A]">
                <button
                    onClick={() => setQrOpen(true)}
                    disabled={t.status !== "confirmed"}
                    data-testid={`ticket-qr-btn-${t.ticket_id}`}
                    className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[#FFFFFF]/40 text-[#FFFFFF] hover:bg-[#FFFFFF]/10 disabled:opacity-40"
                >
                    <QrCode size={12} /> Show QR
                </button>
                {cancelable && (
                    <button
                        onClick={cancel} disabled={busy}
                        data-testid={`ticket-cancel-${t.ticket_id}`}
                        className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[#FAFAFA]/40 text-[#FAFAFA] hover:bg-[#FAFAFA]/10 disabled:opacity-40"
                    >
                        <XCircle size={12} /> Cancel & refund
                    </button>
                )}
            </div>
            {qrOpen && (
                <div
                    role="dialog"
                    className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-4"
                    onClick={() => setQrOpen(false)}
                    data-testid={`ticket-qr-modal-${t.ticket_id}`}
                >
                    <div className="bg-[#111111] border border-[#1A1A1A] rounded-2xl p-6 text-center" onClick={(e) => e.stopPropagation()}>
                        <p className="text-xs text-neutral-400 mb-2">Present this at the door</p>
                        <img
                            src={`${API}/api/tickets/me/${t.ticket_id}/qr.png`}
                            alt="QR"
                            className="w-64 h-64 bg-white p-3 rounded-xl mx-auto"
                        />
                        <p className="text-[10px] text-neutral-500 mt-2 font-mono break-all">{t.ticket_id}</p>
                        <button
                            onClick={() => setQrOpen(false)}
                            className="mt-3 text-xs px-3 py-1.5 rounded-full border border-[#1A1A1A] text-neutral-300"
                        >Close</button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default function AccountTickets() {
    const [tab, setTab] = useState("upcoming");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [disabled, setDisabled] = useState(false);

    const load = useCallback(async () => {
        setLoading(true); setDisabled(false);
        try {
            const status = tab === "canceled" ? "refunded"
                : tab === "past" ? "confirmed" : "confirmed";
            const r = await fetch(`${API}/api/tickets/me?status=${status}&limit=50`, { credentials: "include" });
            if (r.status === 503) { setDisabled(true); setItems([]); }
            else if (r.ok) {
                const d = await r.json();
                let filtered = d.items || [];
                // Client-side filter past vs upcoming (approx)
                if (tab === "past") filtered = filtered.filter((t) => t.checked_in_at || (t.event_ends_at && new Date(t.event_ends_at) < new Date()));
                if (tab === "canceled") {
                    const r2 = await fetch(`${API}/api/tickets/me?status=canceled&limit=50`, { credentials: "include" });
                    if (r2.ok) filtered = filtered.concat((await r2.json()).items || []);
                    const r3 = await fetch(`${API}/api/tickets/me?status=voided&limit=50`, { credentials: "include" });
                    if (r3.ok) filtered = filtered.concat((await r3.json()).items || []);
                }
                setItems(filtered);
            }
        } catch {}
        setLoading(false);
    }, [tab]);

    useEffect(() => { load(); }, [load]);

    if (disabled) {
        return (
            <div className="min-h-screen bg-[#0A0A0A] text-[#FFFFFF] flex items-center justify-center px-4">
                <div className="text-center max-w-md" data-testid="account-tickets-disabled">
                    <Ticket className="text-neutral-500 mx-auto mb-3" size={32} />
                    <h1 className="text-xl font-bold">Ticketing coming soon</h1>
                    <p className="text-neutral-400 text-sm">Paid tickets will show up here once enabled.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0A0A0A] text-[#FFFFFF] font-inter">
            <header className="border-b border-[#1A1A1A] backdrop-blur-md bg-[#0A0A0A]/80 sticky top-0 z-40">
                <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
                    <Link to="/account/subscriptions" className="text-neutral-400 hover:text-neutral-200 flex items-center gap-1 text-sm">
                        <ArrowLeft size={16} /> Account
                    </Link>
                    <span className="text-neutral-600">·</span>
                    <h1 className="text-base font-semibold flex items-center gap-2">
                        <Ticket size={16} className="text-[#FFFFFF]" /> My tickets
                    </h1>
                </div>
            </header>
            <main className="max-w-3xl mx-auto px-4 py-6 space-y-4">
                <div className="inline-flex items-center gap-1 rounded-full bg-[#111111] border border-[#1A1A1A] p-0.5" data-testid="account-tickets-tabs">
                    {["upcoming", "past", "canceled"].map((t) => (
                        <button
                            key={t}
                            onClick={() => setTab(t)}
                            data-testid={`account-tickets-tab-${t}`}
                            className={`text-xs px-3 py-1.5 rounded-full transition ${
                                tab === t ? "bg-[#1F1F1F] text-white" : "text-[#CCCCCC] hover:text-white"
                            }`}
                        >{t}</button>
                    ))}
                </div>
                {loading ? (
                    <p className="text-sm text-neutral-500">Loading…</p>
                ) : items.length === 0 ? (
                    <p className="text-sm text-neutral-500" data-testid="account-tickets-empty">No tickets in this tab.</p>
                ) : (
                    <div className="space-y-3" data-testid="account-tickets-list">
                        {items.map((t) => <TicketRow key={t.ticket_id} t={t} onAction={load} />)}
                    </div>
                )}
            </main>
        </div>
    );
}
